import { Channel, Theme, Topic } from "../../../constants";
import {
  PageContextUtils,
  MessageBrokerUtils,
  StringUtils
} from "../../../utils";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { EmptyComponent } from "../EmptyComponent";
import Loadable from "react-loadable";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useNoScroll } from "../../../hooks";

const { currentName = Theme.Default } = PageContextUtils.getThemeData();

const InnerSpinner = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "spinner/[request]" */

      `./${StringUtils.capitalize(currentName)}/index`
    ),
  loading: EmptyComponent
});

InnerSpinner.preload();

export const VisibleSpinner = ({ fullOverlaySpinner }) => (
  <div
    id="spinner"
    className={classNames("inner-spinner", {
      fullOverlaySpinner: fullOverlaySpinner
    })}
    style={{ display: "flex" }}
  >
    <InnerSpinner />
  </div>
);

VisibleSpinner.propTypes = {
  fullOverlaySpinner: PropTypes.bool
};

const Timeout = {
  SLOW_CONNECTION_TIMEOUT: 20000,
  NORMAL_CONNECTION_TIMEOUT: 15000
};

const isSlowConnection = () => {
  const testConnectionFn =
    window.preloader && window.preloader.isSlowConnection;

  if (typeof testConnectionFn === "function") return testConnectionFn();

  return false;
};
const getTimeout = () => {
  const slowConnection = isSlowConnection();
  return slowConnection
    ? Timeout.SLOW_CONNECTION_TIMEOUT
    : Timeout.NORMAL_CONNECTION_TIMEOUT;
};

const Spinner = () => {
  const spinner = useRef({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const spinnerVisibleSubscription = MessageBrokerUtils.subscribeSync({
      channel: Channel.VIEW_INJECTOR,
      topic: Topic.SetVisible,
      callback: (data, envelope) => {
        console.log("[SPINNER] - Got data:", data, envelope);
        processAction(envelope);
      }
    });
    return () => {
      spinnerVisibleSubscription?.unsubscribe();
    };
  });

  const setErrorState = useCallback(publisher => {
    if (spinner.current.publisher === publisher) {
      const currentSpinner = spinner.current;
      currentSpinner.isError = true;
      currentSpinner.isVisible = false;

      spinner.current = currentSpinner;
      setIsVisible(false);
      console.log(
        `[SPINNER]: setErrorState: error on publisher: ${publisher} `,
        spinner.current
      );
    }
  }, []);

  const updateSpinner = useCallback(
    publisher => {
      if (spinner.current.publisher === publisher && spinner.current?.timer) {
        clearTimeout(spinner.current.timer);
      }

      const timer = setTimeout(() => {
        console.log(
          `[SPINNER] - timeout hit for timer ${timer}, publisher: ${publisher}`
        );
        setErrorState(publisher);
      }, getTimeout());

      const newSpinner = {
        publisher,
        isVisible: true,
        isError: false,
        timer: timer
      };

      console.log(`[SPINNER]: addSpinner: `, newSpinner);

      spinner.current = newSpinner;
      setIsVisible(true);
    },
    [setErrorState, spinner]
  );

  const setErrorOnSpinner = useCallback(
    publisher => {
      if (spinner.current.publisher === publisher) {
        const currentSpinner = spinner.current;
        if (currentSpinner.timer) {
          clearTimeout(currentSpinner.timer);
        }

        spinner.current = {};
        setIsVisible(false);
        console.log(`[SPINNER]: setErrorOnSpinner: `, currentSpinner);
      }
    },
    [spinner]
  );

  const removeSpinner = useCallback(
    publisher => {
      if (spinner.current.publisher === publisher) {
        const currentSpinner = spinner.current;
        if (currentSpinner.timer) {
          clearTimeout(currentSpinner.timer);
          currentSpinner.timer = null;
        }

        spinner.current = {};
        setIsVisible(false);
        console.log(`[SPINNER]: removeSpinner: `, currentSpinner);
      }
    },
    [spinner]
  );

  const processAction = action => {
    const {
      data: { visible, isError },
      headers: { publisher }
    } = action;

    if (visible) {
      if (!isError) {
        updateSpinner(publisher);
      } else {
        setErrorOnSpinner(publisher);
      }
    } else {
      removeSpinner(publisher);
    }
  };

  useNoScroll("inner-spinner", isVisible);
  return isVisible && <VisibleSpinner />;
};

export default Spinner;
